// src/components/HRComponents/CaseTrackerTabs.js
import React, { useMemo } from "react";
import { Briefcase, Clock, Users, ShieldAlert } from "lucide-react";
import { usePermission } from "../../hooks/usePermission";
import { useTabNavigation } from "../../helpers/useTabNavigation";
import CaseTrackerList from "./CaseTrackerList";

const CaseTrackerTabs = () => {
  const { hasPermission, user } = usePermission();

  const tabs = useMemo(() => {
    // 1. Employee-க்கான அடிப்படை தாவல்கள்
    const allTabs = [
      {
        id: "my-raised",
        label: "My Filed Cases",
        icon: <Briefcase className="h-4 w-4" />,
        // description: "Track issues and requests filed by you",
        content: <CaseTrackerList scope="raised" user={user} />
      },
      {
        id: "my-assigned",
        label: "Assigned To Me",
        icon: <Clock className="h-4 w-4" />,
        // description: "Incidents or operations queries assigned to you for resolution",
        content: <CaseTrackerList scope="assigned" user={user} />
      }
    ];

    // 2. HR / Admin / Team Lead-க்கான பிரத்யேக தாவல்கள்
    const adminTabs = [
      {
        id: "team-cases-tab",
        label: "My Team's Cases",
        icon: <Users className="h-4 w-4" />,
        // description: "Monitor and review support cases raised by your direct reports",
        content: <CaseTrackerList scope="team" user={user} />,
        permissionKey: "hr:team-cases-tab",
        action: "view"
      },
      {
        id: "all-cases-tab",
        label: "All Cases Tab",
        icon: <ShieldAlert className="h-4 w-4" />,
        // description: "Supervise, route, and manage all cases within the organization",
        content: <CaseTrackerList scope="all" user={user} />,
        permissionKey: "hr:all-cases-tab",
        action: "view"
      }
    ];

    adminTabs.forEach(tab => {
      // 🔐 ஓனருக்கு எந்த பெர்மிஷனும் தேவையில்லை, அவருக்குத் தானாகவே அணுகல் கிடைக்கும்
      const isOwner = user?.role === "owner";
      
      if (isOwner || hasPermission(tab.permissionKey, tab.action)) {
        allTabs.push(tab);
      }
    });

    return allTabs;
  }, [user, hasPermission]);

  const { activeTab, changeTab } = useTabNavigation(tabs, 'my-raised');
  const activeTabData = tabs.find(tab => tab.id === activeTab) || tabs[0];

  return (   
    <div className="animate-in fade-in duration-300">
      {/* --- TOP TAB NAVIGATION --- */}
      <div className="bg-primary shadow-md sticky top-0 z-20">
        <div className="mx-auto">
          <div className="px-4 lg:px-6">
            <div className="flex items-center justify-between">
              <nav className="flex space-x-2 overflow-x-auto custom-scrollbar pt-2" aria-label="Tabs">
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => changeTab(tab.id)}
                      className={`
                        group inline-flex items-center py-3 px-6 font-bold text-[11px] uppercase tracking-[0.1em]
                        whitespace-nowrap transition-all duration-300 ease-in-out
                        ${isActive
                          ? 'bg-white text-primary rounded-t-xl transform -translate-y-px shadow-sm'
                          : 'text-red-100 hover:text-white hover:bg-red-700 rounded-t-xl'
                        }
                      `}
                    >
                      <span className="mr-2 opacity-70">{tab.icon}</span>
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="hidden lg:block text-[10px] font-bold text-red-100 uppercase tracking-widest opacity-80 italic">
                {activeTabData?.description}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- SUB-TAB MOUNT CONTENT AREA --- */}
      <div className="mx-auto px-4 lg:px-6 py-6">
        <div className="max-w-[1600px] mx-auto">
          {activeTabData?.content}
        </div>
      </div>
    </div>
  );
};

export default CaseTrackerTabs;